import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowsClockwise, MagicWand } from '@phosphor-icons/react';
import { Alert } from '@clay-run/glaze/alert';
import { Button } from '@clay-run/glaze/button';
import { Icon } from '@clay-run/glaze/icon';
import { LoadingBar } from '@clay-run/glaze/loading-bar';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi } from '../../_shared/hooks';
import { getGenerationContext } from '../api-client';
import { DEFAULT_GENERATION_STRATEGY, DEFAULT_MODEL, isThinDescription } from '../copy-generation-rules';
import { generationContextQueryKey } from '../query-keys';
import {
  CAMPAIGN_MOVED_DETAIL,
  canGenerate,
  defaultStatusesFromConditions,
  seedFormFrom,
  tallyText,
} from '../generate-with-ai-logic';
import {
  clearGeneratedCopy,
  failCopyGeneration,
  finishCopyGeneration,
  GENERATION_INCOMPLETE_DETAIL,
  generationSnapshot,
  resolveApprovedSteps,
  reviewTally,
  selectGeneratedCopyEntry,
  stageGeneratedStep,
  startCopyGeneration,
  useGeneratedCopyStore,
} from '../generated-copy-store';
import { reviewStepRows } from '../review-step-summary';
import {
  closeGenerateSession,
  selectGenerateSession,
  setGenerateStage,
  updateGenerateForm,
  useGenerateWithAIStore,
} from '../generate-with-ai-store';
import { generateSequenceCopyStreaming } from '../mutations';
import { usedInPlacesText } from '../usage-gate';
import { isUsageSidebarOpened } from '../usage-sidebar-errors';
import type {
  PlaybooksGenerationInputs,
  PlaybooksSaveSequenceStepsResponse,
  SegmentationConditions,
  SequenceStep,
  SequenceSteps,
} from '../schemas';
import { isVersionConflictError } from '../conflict-errors';
import { GenerateConfigureForm, type GenerateWithAIEmbeddedCampaign } from './GenerateConfigureForm';
import { PushDrawer } from './PushDrawer';
import { stepImageWarnings } from './StepReviewCard';

/**
 * terra-qyt.10 — `handleApply`'s catch needs to recognize the server's 409
 * `version_conflict` (a save-time head move) and show the SAME "discard and
 * regenerate" copy the pre-flight `headVersionId` check below uses, rather
 * than the server's raw `version_conflict` detail text.
 *
 * U20 (M1) — {@link isVersionConflictError} moved to a shared, dependency-free
 * `conflict-errors.ts` (fix round 2 plan, Decision 4): the earlier "kept
 * local since this file can't import a Wave A/B store" reasoning no longer
 * applies — that module is pure (no store, no React), so importing it here
 * doesn't reintroduce whatever constraint required a local copy in the first
 * place, and it is now also the copy `playbook-draft-store.ts` imports.
 */

/** terra-qyt.10 — `handleApply`'s "This sequence changed since you generated" recovery copy, shared by the pre-flight `headVersionId` check and a save-time 409 `version_conflict`. */
const HEAD_MOVED_DETAIL = 'This sequence changed since you generated — discard and regenerate.';

/**
 * terra-qyt round-2 fix U18 (W1) — `handleApply`'s campaign-mismatch refusal
 * copy: the belt-and-suspenders check for a playbook seed swap (or any other
 * host-scope change) that happened while a review sat staged. Distinct copy
 * from {@link HEAD_MOVED_DETAIL} because the underlying problem is different
 * — the sequence content didn't change, the CAMPAIGN the copy was written
 * for did.
 *
 * terra-qyt round-3 fix U24 — the string itself moved to
 * {@link CAMPAIGN_MOVED_DETAIL} in `generate-with-ai-logic.ts` (imported
 * above) so the two `SequencesStep` usage-sidebar decision handlers share it
 * rather than re-declaring or reaching into this component file; this
 * doc comment stays here since `handleApply`'s own check (`:540-548` below)
 * is unchanged.
 */

/**
 * Launcher V2 U7b (terra-l2ai-epic-076.12) — the "Generate with AI" drawer:
 * ONE `PushDrawer` (never a second/nested one — coding-standards §4) whose
 * body swaps between the stage-1 configure form and the stage-2 review
 * footer, driven entirely by `generate-with-ai-store.ts`'s session.
 *
 * ── WHERE THE ACTUAL REVIEW CARDS LIVE (this is NOT here) ─────────────────
 * This drawer's `review` stage renders only a progress summary and the
 * Discard/"Apply to sequence" footer — the per-step Current/Generated
 * cards (U7a's `StepReviewCard`, via `SequenceStepEditor`'s `review` prop)
 * render INLINE in the sequence's own step list, on the page the drawer
 * pushes aside (`PushDrawer`'s whole design: "the page reflows beside the
 * panel... the live page behind them stays interactive"). The drawer is the
 * session's stage machine and the Generate button; the page is where a rep
 * actually reads and edits each step.
 *
 * ── ABORT LIVES HERE, NOT IN THE STORE (Do-Not-Create rule) ────────────────
 * `abortRef` is a plain component ref. Closing the drawer unmounts it
 * (`SequencesStep.tsx` only renders this component while
 * `selectIsSessionOpenFor` is true), so the unmount cleanup below aborts
 * whatever request is in flight. Stale frames are ALSO fenced by
 * `generated-copy-store.ts`'s own request-token check, so an abort that
 * loses the race with an in-flight `onStep` callback is still harmless.
 *
 * ── RESUME-AT-REVIEW (bead Approach: "reopening... resumes at review when
 * an entry is ready") ───────────────────────────────────────────────────────
 * `openGenerateSession` (the trigger, in `SequencesStep.tsx`) always opens a
 * FRESH session at stage `'configure'` — it has no way to know whether a
 * ready review already exists for this scope+sequence. This component makes
 * up that difference itself, once per mount: if a `'ready'` entry already
 * exists the moment it mounts at `'configure'`, it advances straight to
 * `'review'` — the rep does not have to sit through the configure form again
 * just to get back to a review they already have.
 *
 * ── APPLY IN PLACE, NOT A NEW IDENTITY (terra-qyt.10) ──────────────────────
 * The drawer used to advance past `'review'` into a rep-typed `'confirm'`
 * stage that created a brand-new segmentation + sequence (`GenerateConfirmStage`,
 * `confirmGeneratedSequence`). That stage, the store's `names`/`result`
 * fields, and the "(copy)" naming are gone. The review stage's primary
 * action is now "Apply to sequence" ({@link handleApply}): it writes the
 * approved steps onto the CURRENT sequence's head via `props.onApply` — each
 * host's EXISTING gated save (`SequencesStep.tsx#saveWithGate`,
 * `SequenceCopyPane.tsx#saveWithGate`), which already knows how to route a
 * sole-use save inline or a shared save through the usage sidebar. The
 * drawer never writes directly and never creates a new sequence, rung, or
 * version identity (Do-Not-Create).
 */

export interface GenerateWithAIDrawerProps {
  /** The editing SURFACE this session belongs to — a rung's `draftKey`, or `'library'`. Same vocabulary `generated-copy-store.ts` uses. */
  scope: string;
  sequenceId: string;
  surface: 'embedded' | 'library';
  /** Non-`null` on the embedded surface — see `GenerateConfigureForm`'s own doc. */
  campaign: GenerateWithAIEmbeddedCampaign | null;
  /** The rung's segmentation conditions — feeds `defaultStatusesFromConditions` when a campaign's context first resolves. `null` when there is none (e.g. the library surface, or an unsaved rung). */
  rungConditions: SegmentationConditions | null;
  /** The sequence's currently-rendered head steps — the Generate request's scaffold and the empty-sequence gate's source of truth. */
  headSteps: readonly SequenceStep[];
  /**
   * terra-qyt.10 (Data Contract 1/2) — the CURRENT sequence-head version id,
   * compared strictly against `generation.headVersionId` before Apply is
   * allowed to run: a mismatch (including one side being `null` and the
   * other a string) means the sequence changed since Generate ran, and Apply
   * refuses with {@link HEAD_MOVED_DETAIL} rather than overwriting a head the
   * generated copy was never computed against.
   */
  headVersionId: string | null;
  /**
   * terra-qyt round-2 fix U18 (W1) — the campaign the HOST is currently
   * scoped to (the play's locked campaign / the playbook's seed). When
   * provided (not `undefined`), Apply refuses if it differs from the
   * finalized snapshot's `campaignSfdcId`. The library surface omits it —
   * its campaign is the form's own pick, not a host-scoped value there is
   * anything to compare against.
   */
  hostCampaignSfdcId?: string | null;
  /** Called by the header's X ONLY — closes the drawer's visibility (`closeGenerateSession`); the staged review survives (R13). */
  onClose: () => void;
  /** U9's library campaign picker slot — see `GenerateConfigureForm`'s own doc. */
  campaignPicker?: ReactNode;
  /** The sequence's own name — used only in the success toast (`Applied N steps to "<sequenceName>".`). */
  sequenceName: string;
  /**
   * Round-4 QA fixes, U5 (S1b) / Decision 7 — `sequenceSummary.usageCount`
   * (rungs across active playbook heads — the same number Config shows),
   * threaded from both hosts (`SequencesStep.tsx`/`SequenceCopyPane.tsx`).
   * Renders as a "Used in N places" line in the review stage's footer copy
   * and as a suffix on the "Apply to sequence" success toast; `0` for a
   * genuinely unused/not-yet-loaded sequence renders neither (`usedInPlacesText`
   * returns `null` at N<=1).
   */
  usageCount: number;
  /**
   * terra-qyt.10 (Data Contract 8) — the host's EXISTING gated save
   * (`SequencesStep.tsx#saveWithGate`/`SequenceCopyPane.tsx#saveWithGate`).
   * Resolves once the write actually lands (sole-use: saved inline; shared:
   * the usage sidebar's own "Update all"/"Save a new version" write, after
   * the rep decides). Rejects with `UsageSidebarOpenedError` when the host
   * opened the usage sidebar instead of writing immediately — swallowed here
   * without setting an error, since the sidebar took over and the staged
   * review survives for that later write to consume. Any OTHER rejection
   * (a 409 `version_conflict`, a network failure) surfaces inline and
   * leaves the generated review intact so the rep can retry.
   */
  onApply: (steps: SequenceSteps, generationInputs: PlaybooksGenerationInputs) => Promise<PlaybooksSaveSequenceStepsResponse>;
}

export function GenerateWithAIDrawer({
  scope,
  sequenceId,
  campaign,
  rungConditions,
  headSteps,
  headVersionId,
  hostCampaignSfdcId,
  onClose,
  campaignPicker,
  sequenceName,
  usageCount,
  onApply,
}: GenerateWithAIDrawerProps) {
  const { apiFetch } = useApi();
  const session = useGenerateWithAIStore(selectGenerateSession);
  const generation = useGeneratedCopyStore(selectGeneratedCopyEntry(scope, sequenceId));
  const [error, setError] = useState<string | null>(null);
  /** terra-qyt.10 — `handleApply`'s own in-flight flag, mirroring the old stage-3 `isConfirming`. */
  const [isApplying, setIsApplying] = useState(false);

  // StrictMode-resilient: reset at the TOP of the mount effect, not only in
  // cleanup (root CLAUDE.md's "StrictMode-Resilient Effects").
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    abortRef.current = null;
    return () => abortRef.current?.abort();
  }, []);

  const resumedRef = useRef(false);
  useEffect(() => {
    resumedRef.current = false;
  }, []);
  useEffect(() => {
    if (resumedRef.current) return;
    if (session === null || session.stage !== 'configure') return;
    if (generation?.status !== 'ready') return;
    resumedRef.current = true;
    setGenerateStage('review');
  }, [session, generation]);

  const campaignSfdcId = session?.form.campaignSfdcId ?? null;
  // terra-qyt round-2 fix U18 (W1) — `Boolean(campaignSfdcId)` (not `!==
  // null`): playbook mode seeds a session with `campaignSfdcId: ''` before a
  // seed is picked (`SequencesStep.tsx`'s trigger), and `''` is exactly as
  // unfetchable as `null` here — the server's `generation-context` endpoint
  // 400s on a blank query param (`api.ts`'s `requiredQueryParam`). Play mode
  // never has a blank campaign, so this is a no-op change for it.
  const contextQuery = useQuery({
    queryKey: generationContextQueryKey(sequenceId, campaignSfdcId),
    queryFn: () => getGenerationContext(apiFetch, sequenceId, campaignSfdcId as string),
    enabled: Boolean(campaignSfdcId),
  });

  // Seed the form ONCE per campaign — never on every render (bead's own
  // explicit Do-Not-Create rule). `seededForRef` tracks which
  // `campaignSfdcId` this session has already seeded from; resetting it at
  // mount just means a StrictMode remount re-seeds once more, which is
  // idempotent (the same context data lands the same values).
  const seededForRef = useRef<string | null>(null);
  useEffect(() => {
    seededForRef.current = null;
  }, []);

  /**
   * B8 (Wave B, `SequencesStep.tsx`) made the rung's own segmentation fetch
   * `enabled: isSessionOpenHere` — the SAME instant this drawer's own
   * `contextQuery` first enables, rather than at mount (well before this
   * drawer ever exists). The two queries now race, and this drawer's effects
   * (a child's) flush before `SequencesStep`'s own (the parent's) on the SAME
   * commit, so `contextQuery.data` reliably lands ONE commit before
   * `rungConditions` does — `rungConditions` is not yet resolved (still
   * `null`, indistinguishable from "genuinely no conditions") the moment the
   * context-seed effect below runs. `appliedRungDefaultRef` decouples the
   * rung-audience default from that one-shot seed: the context-seed effect
   * marks it `true` immediately whenever it already applied a REAL default
   * (a non-`null` `rungConditions`) or a higher-priority source (lastInputs,
   * a restored snapshot) won instead; the trailing effect below fires ONLY
   * when `rungConditions` itself transitions to non-`null` AND nothing has
   * claimed the field yet — catching exactly this late-arrival case without
   * ever clobbering a higher-priority seed.
   */
  const appliedRungDefaultRef = useRef(false);
  useEffect(() => {
    appliedRungDefaultRef.current = false;
  }, []);

  /**
   * D6 (C1) — a fresh `openGenerateSession` call always seeds the form from
   * `seed`/blank (it has no way to know a staged generation already exists
   * for this scope+sequence — see this file's own module doc, "RESUME-AT-
   * REVIEW"). Mirrors that exact idiom for the FORM, not just the stage: once
   * per mount, if a snapshot already exists (either surface — the drawer was
   * closed mid- or post-generation and just reopened), restore `form` from
   * it and mark `seededForRef` as already-seeded for that campaign so the
   * context-seed effect below never clobbers it once `contextQuery` resolves.
   * D20 — `handleConfirm` (below) reads the FINALIZED snapshot
   * (`generation.inputs`, set by `finishCopyGeneration` once `done` lands),
   * never `session.form` directly, once a generation exists for this key —
   * this restore effect exists only to make the FORM (what the rep sees and
   * can still edit before Generate) agree with that snapshot across a
   * close/reopen, not because confirm reads it.
   */
  const snapshot = useGeneratedCopyStore(generationSnapshot(scope, sequenceId));
  const restoredFromSnapshotRef = useRef(false);
  useEffect(() => {
    restoredFromSnapshotRef.current = false;
  }, []);
  useEffect(() => {
    if (restoredFromSnapshotRef.current || snapshot === null) return;
    restoredFromSnapshotRef.current = true;
    seededForRef.current = snapshot.campaignSfdcId;
    // A restored snapshot's `memberStatuses` is the rep's own generation
    // input — never let a late-arriving rung default (see this ref's own
    // doc above) override it.
    appliedRungDefaultRef.current = true;
    updateGenerateForm({
      campaignSfdcId: snapshot.campaignSfdcId,
      eventName: snapshot.eventName,
      campaignDescription: snapshot.campaignDescription,
      memberStatuses: snapshot.memberStatuses,
      instruction: snapshot.instruction,
      sourceMaterial: snapshot.sourceMaterial,
    });
  }, [snapshot]);

  useEffect(() => {
    // D20 (Codex F-F) — once a generation exists for this key (pending,
    // ready, or error), the form is FROZEN for this session: nothing here
    // may patch it, no matter how late `contextQuery` or `rungConditions`
    // resolves. Confirm reads the finalized snapshot, never this form, but
    // the form is still what renders on screen (e.g. a rep who reopens the
    // drawer mid-review) — a late seed silently rewriting it out from under
    // the rep, even if harmless to the eventual write, is still a real bug.
    if (generation !== undefined) return;
    // terra-qyt round-2 fix U18 (W1) — `!campaignSfdcId` mirrors the query's
    // own `Boolean(campaignSfdcId)` gate above: a blank seed means
    // `contextQuery` never fired, so `contextQuery.data` would be `undefined`
    // anyway, but this guard makes the "no campaign yet" reason explicit
    // rather than relying on that indirection.
    if (contextQuery.data === undefined || !campaignSfdcId) return;
    if (seededForRef.current === campaignSfdcId) return;
    seededForRef.current = campaignSfdcId;
    const data = contextQuery.data;
    // C6/A9 — a fresh session (no restored snapshot above) prefers the
    // sequence's own `preset.lastInputs` over the campaign's raw defaults,
    // but ONLY when the preset was recorded for THIS SAME campaign (a rep
    // can pick a different campaign for the same sequence on a later run —
    // `seedFormFrom`'s own doc: confirming the campaign match is the
    // caller's job, not that function's).
    const lastInputs =
      data.preset !== null && data.preset.campaignSfdcId === campaignSfdcId ? data.preset.lastInputs : null;
    const seed = seedFormFrom({
      lastInputs,
      context: {
        eventNameSeed: data.eventNameSeed,
        campaign: { description: data.campaign.description },
        defaultInstruction: data.defaultInstruction,
      },
    });
    // `lastInputs` winning claims the field outright (never overridden by a
    // later-arriving rung default); otherwise this only counts as CLAIMED if
    // `rungConditions` is a REAL answer already — `null` here is ambiguous
    // (see `appliedRungDefaultRef`'s own doc) and leaves the field open for
    // the trailing effect below to fill in once it resolves.
    if (lastInputs !== null || rungConditions !== null) appliedRungDefaultRef.current = true;
    updateGenerateForm({
      ...seed,
      // The embedded surface's rung-audience default only applies when
      // there's no prior generation to prefer instead — `seedFormFrom`'s own
      // no-`lastInputs` fallback has no notion of a rung's segmentation
      // conditions (isomorphic, campaign-only), so this stays a drawer-level
      // override layered on top, exactly like before C6.
      memberStatuses: lastInputs !== null ? seed.memberStatuses : defaultStatusesFromConditions(rungConditions),
    });
  }, [contextQuery.data, campaignSfdcId, rungConditions]);

  // See `appliedRungDefaultRef`'s own doc — catches `rungConditions`
  // resolving to non-`null` on a LATER commit than the context-seed effect
  // above (B8's race). A no-op once anything has already claimed the field.
  useEffect(() => {
    // D20 (Codex F-F) — same freeze-once-a-generation-exists rule as the
    // context-seed effect above. This is the exact scenario Codex traced: a
    // rep generates for "Attended" while `rungConditions` is still
    // unresolved; without this guard, `rungConditions` resolving to a real
    // (different) default LATER — after the generation already produced
    // copy for "Attended" — would silently rewrite the form to whatever the
    // rung's OWN default is, mid-review.
    if (generation !== undefined) return;
    if (appliedRungDefaultRef.current || rungConditions === null) return;
    appliedRungDefaultRef.current = true;
    updateGenerateForm({ memberStatuses: defaultStatusesFromConditions(rungConditions) });
  }, [rungConditions, generation]);

  if (session === null) return null;

  const isGenerating = generation?.status === 'pending';
  /** D19 (Codex F-E) — a FRESH incomplete generation with no prior review to fall back to; gates the review stage's footer/body (see those blocks' own docs). */
  const isErrorEntry = generation?.status === 'error';
  const isEmptySequence = headSteps.length === 0;
  const tally = reviewTally(generation);
  /**
   * terra-qyt.12 (U8) — the review-stage body's per-step decision list: one
   * jump-to-card row per matching head step, ascending. `reviewStepRows`
   * itself returns `[]` for an error entry, matching every other
   * `generation`-derived value in this stage.
   */
  const stepRows = generation !== undefined ? reviewStepRows(generation, headSteps) : [];
  /** D8 (C4) — how many staged reviews currently have a dropped/duplicated image, for the review-stage tally line. */
  const imageWarningCardCount =
    generation?.status === 'ready'
      ? Object.values(generation.steps).filter((review) => {
          const warnings = stepImageWarnings(review);
          return warnings.dropped.length > 0 || warnings.duplicated.length > 0;
        }).length
      : 0;

  async function handleGenerate() {
    setError(null);
    const form = session!.form;
    const body = {
      campaignSfdcId: form.campaignSfdcId ?? '',
      instruction: form.instruction,
      eventName: form.eventName,
      campaignDescription: form.campaignDescription,
      memberStatuses: form.memberStatuses,
      sourceMaterial: form.sourceMaterial,
    };
    // D6 (C1) — snapshot THIS run's own inputs onto the pending entry
    // (`startCopyGeneration`'s own `inputs` param, A3) so a close/reopen
    // restores the form from what actually produced the staged copy, not
    // whatever this drawer's seed effects would otherwise guess (see the
    // snapshot-restore effect above). `sourceHeadVersionId` isn't known
    // until the stream's `done` frame lands — `handleConfirm` reads the
    // REAL value off `generation.headVersionId` at that point, never this
    // placeholder.
    const inputs: PlaybooksGenerationInputs = {
      campaignSfdcId: body.campaignSfdcId,
      eventName: body.eventName,
      campaignDescription: body.campaignDescription,
      memberStatuses: body.memberStatuses,
      instruction: body.instruction,
      sourceMaterial: body.sourceMaterial,
      model: DEFAULT_MODEL,
      strategy: DEFAULT_GENERATION_STRATEGY,
      sourceHeadVersionId: null,
      generatedAt: new Date().toISOString(),
    };
    const token = startCopyGeneration(scope, sequenceId, true, inputs);
    if (token === null) return;
    const controller = new AbortController();
    abortRef.current = controller;
    const headByPosition = new Map(headSteps.map((step) => [step.position, step]));
    try {
      const result = await generateSequenceCopyStreaming(apiFetch, sequenceId, body, {
        onStep: (step) =>
          stageGeneratedStep(scope, sequenceId, token, step, headByPosition.get(step.position)),
        signal: controller.signal,
      });
      // D19 (Codex F-E) — `result.positions` (the `done` frame's own list of
      // positions the server generated for) is now REQUIRED, never omitted:
      // `finishCopyGeneration`'s completeness gate is what refuses a
      // partial/empty review from ever reaching `'ready'`. Its `complete`
      // return tells us whether to surface the retry detail — a FRESH
      // incomplete run becomes a `GeneratedCopyErrorEntry` (rendered below);
      // an incomplete REGENERATE instead restores the prior review WHOLE
      // (module doc, Codex NEW-INCOMPLETE-REGENERATE-LOSES-REVIEW) and still
      // needs this same banner alongside it.
      const { complete } = finishCopyGeneration(scope, sequenceId, token, {
        headVersionId: result.headVersionId,
        thinContext: result.thinContext,
        strategy: result.strategy,
        positions: result.positions,
      });
      if (!complete) {
        setError(GENERATION_INCOMPLETE_DETAIL);
      }
      setGenerateStage('review');
    } catch (thrown) {
      const wasCurrent = failCopyGeneration(scope, sequenceId, token);
      if (wasCurrent) {
        setError(
          thrown instanceof Error ? thrown.message : 'Could not generate copy for this Sequence — try again.',
        );
      }
    }
  }

  /**
   * D20 (Codex F-F) — the SINGLE source stage 3 (confirm) reads for every
   * generation-request field: `generation.inputs`, the snapshot
   * `finishCopyGeneration` finalized when `done` landed (`sourceHeadVersionId`/
   * `strategy` real, `generatedAt` the original Generate-click moment — see
   * that function's own doc). NEVER `session.form` once that snapshot
   * exists — the form may have drifted since Generate was clicked (a
   * late-arriving rung default, `lastInputs`, or context seed; those seed
   * effects now refuse to fire once a generation exists, but this is the
   * belt-and-suspenders read-side half of that same fix).
   *
   * Only falls back to deriving from the live form when `generation.inputs`
   * is itself absent — an entry from before D6, or a hand-constructed test
   * fixture that never supplied one — matching every OTHER optional-`inputs`
   * fallback in this codebase (`generated-copy-store.ts`'s own doc).
   *
   * Callers must only invoke this once `generation?.status === 'ready'` —
   * unchecked here since both call sites already guard it.
   */
  function currentGenerationInputs(): PlaybooksGenerationInputs {
    if (generation?.status === 'ready' && generation.inputs) return generation.inputs;
    if (session === null) {
      // Unreachable by construction: stage 3 only renders inside an open
      // session. Named error rather than a silent empty request.
      throw new Error('Generate with AI: no open session to read inputs from');
    }
    const form = session.form;
    return {
      campaignSfdcId: form.campaignSfdcId ?? '',
      eventName: form.eventName,
      campaignDescription: form.campaignDescription,
      memberStatuses: form.memberStatuses,
      instruction: form.instruction,
      sourceMaterial: form.sourceMaterial,
      model: DEFAULT_MODEL,
      strategy: (generation?.status === 'ready' ? generation.strategy : undefined) ?? DEFAULT_GENERATION_STRATEGY,
      sourceHeadVersionId: generation?.status === 'ready' ? generation.headVersionId : null,
      generatedAt: new Date().toISOString(),
    };
  }

  /**
   * terra-qyt.10 — "Apply to sequence" (review's primary action). Writes the
   * approved steps IN PLACE onto the CURRENT sequence's head via
   * `props.onApply` (the host's existing gated save) — no confirm stage, no
   * new identity. Guarded on `generation.status === 'ready'` and
   * `tally.pending === 0` (both already gate the button itself; re-checked
   * here as defense in depth).
   *
   * `generation.headVersionId !== headVersionId` catches a head that moved
   * since Generate ran (the generated copy is no longer computed against the
   * current head) — refuses BEFORE spending a write, with the same
   * `HEAD_MOVED_DETAIL` copy a save-time 409 `version_conflict` also
   * produces (see the catch below), so the rep sees one consistent recovery
   * message for both cases: discard and regenerate.
   */
  async function handleApply() {
    if (generation?.status !== 'ready' || tally.pending !== 0) return;
    if (generation.headVersionId !== headVersionId) {
      setError(HEAD_MOVED_DETAIL);
      return;
    }
    // terra-qyt round-2 fix U18 (W1) — belt-and-suspenders campaign check:
    // `hostCampaignSfdcId` is the host's CURRENT scope (a play's locked
    // campaign, or a playbook's live seed); `generation.inputs.campaignSfdcId`
    // is whatever campaign the review was actually generated FOR. A mismatch
    // means the host's scope moved since Generate ran (the seed-change path
    // already drops most staged reviews outright — this catches whatever
    // slips past that, and any future host that doesn't clear on scope
    // change). `undefined` on either side skips the check: the prop is
    // `undefined` on the library surface (no host scope to compare against)
    // and `inputs` is `undefined` only on pre-D6 fixtures/entries that never
    // recorded one.
    if (
      hostCampaignSfdcId !== undefined &&
      generation.inputs !== undefined &&
      generation.inputs.campaignSfdcId !== (hostCampaignSfdcId ?? '')
    ) {
      addErrorToast({ content: CAMPAIGN_MOVED_DETAIL });
      setError(CAMPAIGN_MOVED_DETAIL);
      return;
    }
    setError(null);
    const steps = resolveApprovedSteps(generation, headSteps);
    // D20 — every generation-request field comes from the finalized
    // snapshot, never `session.form` (see `currentGenerationInputs`'s own
    // doc).
    const generationInputs = currentGenerationInputs();
    const acceptedCount = Object.values(generation.steps).filter(
      (review) => review.decision === 'accepted',
    ).length;
    setIsApplying(true);
    try {
      await onApply(steps, generationInputs);
    } catch (thrown) {
      // "Gate took over" — `openUsageSidebar` already called
      // `closeGenerateSession()` (`usage-sidebar-store.ts:192`), so this
      // drawer is unmounting; nothing here may still set state. The staged
      // review survives in `generated-copy-store.ts` (R13) and reopening
      // Generate resumes at review (`resumedRef`, above) either once the
      // sidebar's own "Update all"/"Save a new version" write finishes and
      // clears it, OR sooner, if the rep dismisses the sidebar's X / clicks
      // its "Keep reviewing instead" control — both record a `'resume-review'`
      // decision that the origin consumes via `usage-sidebar-resume.ts`
      // (Codex fixes, 2026-09-17) — the same reopening path, reached after
      // the origin is mounted and settled.
      if (isUsageSidebarOpened(thrown)) return;
      setError(
        isVersionConflictError(thrown)
          ? HEAD_MOVED_DETAIL
          : thrown instanceof Error
            ? thrown.message
            : 'Could not apply — try again.',
      );
      return;
    } finally {
      setIsApplying(false);
    }
    // Round-4 QA fixes, U5 (S1b) / Decision 7 — the discoverability suffix,
    // only when the sequence is actually shared (`usedInPlacesText` is
    // `null` at N<=1, matching the header badge/footer's own threshold).
    const placesText = usedInPlacesText(usageCount);
    addToast({
      content: `Applied ${acceptedCount} steps to "${sequenceName}".${placesText ? ` ${placesText}.` : ''}`,
      status: 'success',
    });
    // Order matches the old `handleDone` — irrelevant which runs first, both
    // must happen once the write is confirmed to have landed.
    clearGeneratedCopy(scope, sequenceId);
    closeGenerateSession();
  }

  function handleDiscard() {
    clearGeneratedCopy(scope, sequenceId);
    setGenerateStage('configure');
  }

  /**
   * terra-qyt.12 (U8) — jumps the page to a step's inline `StepReviewCard`
   * (rendered by `SequenceStepEditor`, wrapped in `<div data-step-review={…}>`
   * — do-NOT-create rule: no `useRef`-per-step registry, one `querySelector`
   * at click time is correct since exactly one editor surface with these
   * anchors is mounted per session (Codex challenge triage, resolved). The
   * `typeof document` guard keeps this module bun-safe even though it's a
   * `.tsx` file (coding-standards do-NOT-create rule).
   */
  function handleJumpToStep(position: number) {
    if (typeof document === 'undefined') return;
    document.querySelector(`[data-step-review="${position}"]`)?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });
  }

  const subtitle = session.stage === 'configure' ? 'Set up your generation' : tallyText(tally);

  const footer =
    session.stage === 'configure' ? (
      <>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleGenerate()}
          disabled={!canGenerate(session.form) || isGenerating || isEmptySequence}
          loading={isGenerating}
        >
          <Icon type={isGenerating ? ArrowsClockwise : MagicWand} />
          {isGenerating ? 'Generating…' : 'Generate'}
        </Button>
      </>
    ) : session.stage === 'review' ? (
      <>
        <Button variant="secondary" onClick={handleDiscard}>
          Discard all generated
        </Button>
        {/*
          D13 (C5) — re-runs Generate against the SAME snapshot (`session.form`
          is frozen since the last time `configure` was showing, D6) with
          `keepAccepted: true` (`startCopyGeneration`'s own default, A2) —
          `stageGeneratedStep`'s existing guard refuses to overwrite any
          position that already carries an `'accepted'` decision, so this
          re-stages only the remaining (pending/rejected/unchanged)
          positions without a second, different code path.

          D19 (Codex F-E) — a FRESH incomplete generation lands here as a
          bare `GeneratedCopyErrorEntry` with nothing to "regenerate
          remaining" FROM, so this same button reads "Retry" instead —
          `handleGenerate` is identical either way (it reads
          `startCopyGeneration`'s own `previous` field, which is `null` for
          this case, so it starts fully fresh).
        */}
        <Button variant="secondary" onClick={() => void handleGenerate()} disabled={isGenerating} loading={isGenerating}>
          <Icon type={isGenerating ? ArrowsClockwise : MagicWand} />
          {isErrorEntry ? 'Retry' : 'Regenerate remaining'}
        </Button>
        {/* D19 (Codex F-E) — never exposes "Apply to sequence" while the entry is an error (nothing reviewed, nothing to apply). */}
        {!isErrorEntry && (
          <Button
            variant="primary"
            onClick={() => void handleApply()}
            disabled={tally.pending !== 0}
            loading={isApplying}
          >
            Apply to sequence
          </Button>
        )}
      </>
    ) : null;

  return (
    <PushDrawer title="Generate with AI" subtitle={subtitle} onClose={onClose} footer={footer}>
      {session.stage === 'configure' ? (
        <>
          {isEmptySequence && <Alert status="warning">No steps on this sequence yet.</Alert>}
          {/*
            C7 (Codex #14, cheap version) — every generation this UI ever
            issues requests the `'whole'` strategy (`DEFAULT_GENERATION_STRATEGY`;
            there is no per-step UI path yet, follow-up 5); the server falls
            back to `'per-step'` on its own when the whole call returns the
            wrong step set, and the `done` frame's `strategy` reports what
            actually ran (2026-09-17 QA follow-ups, U2). Absent that fallback
            there is no real "step k of N" progress to report — the server
            writes the whole sequence in one model call and only the LAST
            frame (`done`) ever lands. An indeterminate bar + "Writing all N
            steps…" says what's actually happening; a fake per-step counter that never
            advances until the very end is worse than no counter.
          */}
          {isGenerating && (
            <div className="flex flex-col gap-2">
              <LoadingBar />
              <Text size="sm" variant="tertiary">{`Writing all ${headSteps.length} steps…`}</Text>
            </div>
          )}
          {error && (
            <Alert status="error">
              <div className="flex flex-col items-start gap-2">
                <Text size="sm">{error}</Text>
                <Button variant="secondary" size="sm" onClick={() => void handleGenerate()}>
                  Retry
                </Button>
              </div>
            </Alert>
          )}
          <GenerateConfigureForm
            campaign={campaign}
            campaignPicker={campaignPicker}
            form={session.form}
            onChange={updateGenerateForm}
            context={contextQuery.data ?? null}
            contextSettled={contextQuery.isSuccess || contextQuery.isError}
            contextError={
              contextQuery.isError
                ? contextQuery.error instanceof Error
                  ? contextQuery.error.message
                  : 'Could not load campaign context — try again.'
                : null
            }
          />
        </>
      ) : session.stage === 'review' ? (
        <div className="flex flex-col gap-3">
          <Text size="sm">{tallyText(tally)}</Text>
          {/* U5 (S1b, Decision 7) — the SAME discoverability line the editor header's badge and the apply toast show. */}
          {usedInPlacesText(usageCount) && (
            <Text size="sm" variant="tertiary">
              {usedInPlacesText(usageCount)}
            </Text>
          )}
          {/*
            terra-qyt.12 (U8) — the per-step decision list + jump links. The
            per-step Current/Generated cards themselves stay on the page
            beside this (non-modal) push drawer (this file's own doc,
            :73-81) — these rows are ONLY navigation, never a second copy of
            the cards' content.
          */}
          {stepRows.length > 0 && (
            <div className="flex flex-col items-start gap-1">
              {stepRows.map((row) => (
                <Button
                  key={row.position}
                  variant="inline-text"
                  size="xs"
                  onClick={() => handleJumpToStep(row.position)}
                >
                  {`Step ${row.ordinal} · ${row.label}`}
                </Button>
              ))}
              <Text size="sm" variant="tertiary">
                Review each step&apos;s Current / Generated cards on the page beside this panel.
              </Text>
            </div>
          )}
          {/*
            D19 (Codex F-E / NEW-INCOMPLETE-REGENERATE-LOSES-REVIEW) — `error`
            is set here (not just in the `configure` stage, its original
            home) whenever `finishCopyGeneration` reports `complete: false`:
            a FRESH incomplete run has NOTHING else to show in this stage
            (the entry is a bare `GeneratedCopyErrorEntry`, no steps), so
            this banner IS the review stage's whole story; an incomplete
            REGENERATE restores the prior review WHOLE and shows this banner
            ALONGSIDE it, rather than losing the rep's prior decisions.
          */}
          {/*
            No inline Retry button here (unlike the `configure` stage's own
            error banner below) — the review-stage FOOTER already carries
            the retry action (labeled "Retry" for a fresh error entry,
            "Regenerate remaining" otherwise); a second button with the same
            accessible name would make `getByRole('button', { name: 'Retry' })`
            ambiguous.
          */}
          {error && <Alert status="error">{error}</Alert>}
          {/*
            D8 (C4) — the tally footer's own count of cards with an image
            warning, derived from the SAME `staged`/`edited` fields
            `StepReviewCard`'s own chips read (`stepImageWarnings`,
            `StepReviewCard.tsx`) — no separate store, no cross-component
            wiring: this drawer and every review card independently recompute
            the identical answer from `generated-copy-store.ts`'s existing
            entry.
          */}
          {imageWarningCardCount > 0 && (
            <Text size="sm" variant="tertiary">{`${imageWarningCardCount} with image warnings`}</Text>
          )}
          {generation?.status === 'ready' &&
            generation.thinContext &&
            isThinDescription(session.form.campaignDescription) && (
              <Alert status="warning">
                Generated copy may invent details — add context or paste source material below.
              </Alert>
            )}
        </div>
      ) : null}
    </PushDrawer>
  );
}
